import React, { ComponentType } from 'react';
import { useIsTeacher } from '@reducers/user/hooks';
import { usePlatformEventOnMount } from './platformEvents';
import { PLATFORM_EVENT_TYPES } from '../constants/platformEvents.generated';

interface WithMatchProps {
  match?: { params?: Record<string, string | undefined> };
}

/**
 * Emits a single `practice_reviewed` platform event on mount, reading
 * attribution from the route's match params. Shared by the practice-review
 * pages so the context shape (practice_type derivation, reviewer_role, ids)
 * lives in one place instead of being reimplemented per component. Not
 * role-gated: student self-review is intended, and `reviewer_role`
 * distinguishes teacher vs student for analytics.
 */
const withPracticeReviewedEvent = <P extends WithMatchProps>(
  WrappedComponent: ComponentType<P>,
): React.FC<P> => {
  const ComponentWithPracticeReviewedEvent: React.FC<P> = (props: P) => {
    const isTeacher = useIsTeacher();
    const { match } = props;
    const { sectionId, lessonId, unitId, practiceSessionId, studentId } =
      match?.params || {};

    usePlatformEventOnMount(
      PLATFORM_EVENT_TYPES.PRACTICE_REVIEWED,
      { sectionId, lessonId },
      {
        practice_type: lessonId ? 'lesson_practice' : 'unit_practice',
        ...(unitId ? { unit_id: unitId } : {}),
        ...(practiceSessionId ? { practice_session_id: practiceSessionId } : {}),
        ...(studentId ? { student_id: studentId } : {}),
        reviewer_role: isTeacher ? 'teacher' : 'student',
      },
    );

    return <WrappedComponent {...props} />;
  };
  return ComponentWithPracticeReviewedEvent;
};

export default withPracticeReviewedEvent;
